import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { Map as LeafletMap } from 'leaflet';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { useMount } from 'react-use';
import { getWindow, canUseDOM } from '@surfline/web-common';

import { setMapLocation, zoomIntoLocation } from 'actions/map';
import { clearActiveSpot, setActiveSpot } from 'actions/animations';
import MapControls from 'components/MapControls';
import useMap from 'hooks/map/useMap';
import { NO_SYNC, ALWAYS_SYNC, SYNC_ONLY_ON_MAP_INTERACTIONS } from 'propTypes/map/mapUrlSync';
import type { AppState } from 'stores';
import { useAppSelector } from 'stores/hooks';
import type { Camera } from 'types/camera';
import type { Location } from 'types/map';
import type { Units } from 'types/units';
import mapLayers from 'utils/mapLayers';
import { isMapPage, isCurrentPageMapFromWindow } from 'utils/mapRouteUtils';
import { stripAtCharacterFromLocation } from 'utils/urls/mapPaths';

// Import extracted components and hooks
import { MapOverlay } from './components';
import {
  useMapInitialization,
  useMapPermissions,
  useMapMarkers,
  useSpotDataFetching,
} from './hooks';

import styles from './SpotMap.module.scss';

// Function to parse location from URL path - moved outside component
const parseLocationFromUrl = (urlPath: string) => {
  try {
    // Extract location part from URL path like /surf-reports-forecasts-cams-map/@33.5,-117.7,8z
    const locationMatch = urlPath.match(/\/surf-reports-forecasts-cams-map\/(@?.+)/);
    if (!locationMatch || !locationMatch[1]) return null;

    const locationPart = locationMatch[1];
    const cleanLocation = stripAtCharacterFromLocation(locationPart);
    const pathLocation = cleanLocation.split(',');

    if (pathLocation && pathLocation.length >= 3) {
      const lat = Number(pathLocation[0]);
      const lon = Number(pathLocation[1]);
      const zoom = Number(pathLocation[2].replace('z', ''));

      // Only return if we have valid numbers
      if (!Number.isNaN(lat) && !Number.isNaN(lon) && !Number.isNaN(zoom)) {
        return {
          center: {
            lat,
            lon,
          },
          zoom,
        };
      }
    }
    return null;
  } catch (error) {
    console.warn('Failed to parse location from URL:', error);
    return null;
  }
};

interface SpotData {
  _id: string;
  lat: number;
  lon: number;
  waveHeight: {
    min: number;
    max: number;
  };
  conditions: {
    value: string;
  };
  cameras: Camera[];
  zone?: {
    title: string;
  };
  title?: string;
}

interface AnimationsState {
  activeSpotId: string | null;
}

interface MapOptions {
  maxZoom: number;
  minZoom: number;
}

// Define the sync URL type using string literal types
type MapUrlSyncType = typeof NO_SYNC | typeof ALWAYS_SYNC | typeof SYNC_ONLY_ON_MAP_INTERACTIONS;

interface SpotMapProps {
  mapOptions?: MapOptions;
  showBottomControls?: boolean;
  clusterMaxZoom?: number;
  onClickSpotMarker: (spot: SpotData) => void;
  syncUrl?: MapUrlSyncType;
  storeLocationInSession?: boolean;
  currentSpotId?: string | null;
  onLoadingChange?: (isLoading: boolean) => void;
}

const SpotMapComponent: React.FC<SpotMapProps> = ({
  mapOptions = {
    maxZoom: 18,
    minZoom: 4,
  },
  showBottomControls = false,
  clusterMaxZoom = 0,
  onClickSpotMarker,
  syncUrl = SYNC_ONLY_ON_MAP_INTERACTIONS,
  storeLocationInSession = false,
  currentSpotId = null,
  onLoadingChange,
}) => {
  // Hooks
  const router = useRouter();
  const dispatch = useDispatch();
  const permissions = useMapPermissions();

  // Map state refs
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingSpots, setIsLoadingSpots] = useState(false);
  const isExternalChangeRef = useRef(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const onMoveStartRef = useRef<(() => void) | null>(null);
  const onMoveEndRef = useRef<(() => void) | null>(null);
  const leafletMapRef = useRef<LeafletMap | null>(null);
  const LRef = useRef(null);

  // Redux selectors
  const map = useAppSelector((state: AppState) => state.map);
  const units = useAppSelector((state: AppState) => state.units);
  const animations = useAppSelector((state: AppState) => state.animations) as AnimationsState;

  // Global map state
  useMap();

  // Store previous values for comparison
  const prevActiveSpotIdRef = useRef(animations.activeSpotId);

  // Action dispatchers
  const doSetMapLocation = useCallback(
    (location: Location, syncUrlFlag: boolean, storeInSession: boolean) => {
      // Only sync URL if we're actually on a map page
      const isOnMapPage = isMapPage(router.asPath);

      if (!isOnMapPage && syncUrlFlag) {
        // Call without URL sync if we're not on a map page
        dispatch(setMapLocation(location, router, false, storeInSession) as any);
      } else {
        dispatch(setMapLocation(location, router, syncUrlFlag, storeInSession) as any);
      }
    },
    [dispatch, router],
  );

  const doZoomIntoLocation = useCallback(
    (center: { lat: number; lon: number }, syncUrlFlag: boolean) => {
      dispatch(zoomIntoLocation(center, router, syncUrlFlag) as any);
    },
    [dispatch, router],
  );

  const doSetActiveSpot = useCallback(
    (spotId: string) => {
      dispatch(setActiveSpot(spotId));
    },
    [dispatch],
  );

  const doClearActiveSpot = useCallback(() => {
    dispatch(clearActiveSpot());
  }, [dispatch]);

  // Initialize spot data fetching first
  const { updateSpots } = useSpotDataFetching({
    leafletMapRef,
    clusterMaxZoom,
    onFetchStart: useCallback(() => {
      setIsLoadingSpots(true);
      if (onLoadingChange) onLoadingChange(true);
    }, [onLoadingChange]),
    onFetchComplete: useCallback(() => {
      setIsLoadingSpots(false);
      if (onLoadingChange) onLoadingChange(false);
    }, [onLoadingChange]),
  });

  // Utility functions
  const findSubregionById = useCallback(
    (id: string) => {
      const subregionMap = new Map<string, string>(
        map.subregions.subregions.map((subregion: { _id: string; subregion: string }) => [
          subregion._id,
          subregion.subregion,
        ]),
      );
      return subregionMap.get(id) || null;
    },
    [map.subregions],
  );

  // Initialize map with custom hook
  const { spotsUpdateScheduledRef, initializeMap, bindEventHandlers } = useMapInitialization({
    mapContainerRef,
    location: {
      center: {
        lat: map.location.center?.lat ?? 0,
        lon: map.location.center?.lon ?? 0,
      },
      zoom: map.location.zoom,
    } as Location,
    mapOptions,
    onMoveStartRef,
    onMoveEndRef,
    updateSpots,
    leafletMapRef,
    LRef,
  });

  // Get marker utilities
  const { getMarkerStyle, getTooltipPosition } = useMapMarkers(
    leafletMapRef,
    LRef,
    animations.activeSpotId,
    currentSpotId,
  );

  useMount(() => {
    // Define move handlers and store in refs
    // Create moveStart handler
    onMoveStartRef.current = () => {
      setIsDragging(true);
    };

    // Create moveEnd handler
    onMoveEndRef.current = () => {
      if (!leafletMapRef.current) return;

      // Check if we're still on a map page before processing moveend events
      // Use window.location.pathname to get current route (not captured in closure)
      const isOnMapPage = isCurrentPageMapFromWindow();

      if (!isOnMapPage) {
        return;
      }

      try {
        const { lat, lng } = leafletMapRef.current.getCenter();
        const currentZoom = leafletMapRef.current.getZoom();

        // Only update Redux state if position/zoom actually changed significantly
        const isSignificantLatChange = Math.abs(lat - (map.location.center?.lat ?? 0)) > 0.001;
        const isSignificantLonChange = Math.abs(lng - (map.location.center?.lon ?? 0)) > 0.001;
        const isZoomChange = currentZoom !== map.location.zoom;

        if (isSignificantLatChange || isSignificantLonChange || isZoomChange) {
          doSetMapLocation(
            {
              center: {
                lat,
                lon: lng,
              },
              zoom: currentZoom,
            },
            syncUrl === ALWAYS_SYNC ||
              (syncUrl === SYNC_ONLY_ON_MAP_INTERACTIONS && !isExternalChangeRef.current),
            storeLocationInSession,
          );

          // Schedule spots update only if position changed significantly or we zoomed
          if (isSignificantLatChange || isSignificantLonChange || isZoomChange) {
            spotsUpdateScheduledRef.current = true;
            setIsLoadingSpots(true); // Set loading state before updating spots
            updateSpots();
          }
        }

        setIsDragging(false);
        isExternalChangeRef.current = false;
      } catch (_error) {
        setIsDragging(false);
        setIsLoadingSpots(false);
      }
    };

    // Apply the handlers to the map
    bindEventHandlers();

    // Initialize   map when component mounts - mimics componentDidMount behavior
    const win = getWindow();

    // Handle footer
    if (canUseDOM && win.document.getElementById('backplane-footer')) {
      const footer = win.document.getElementById('backplane-footer');
      if (footer) {
        footer.style.display = 'none';
      }
    }

    doClearActiveSpot();

    if (canUseDOM && !/HeadlessChrome/.test(win.navigator.userAgent)) {
      // Same approach as original component
      const L = require('leaflet');
      LRef.current = L;

      // Prepare a handler for URL changes
      const routeHandler = () => {
        // When route changes, clear any active spot
        doClearActiveSpot();

        // Get URL directly from window for more reliability during client-side transitions
        const currentPathname = win.location.pathname;
        const urlLocation = parseLocationFromUrl(currentPathname);

        if (urlLocation && urlLocation.center.lat !== 0 && urlLocation.center.lon !== 0) {
          // Clear session storage to prevent override
          try {
            win.sessionStorage.removeItem('slMapLocation');
          } catch (_err) {
            // Ignore errors
          }

          // Direct Redux update to match URL
          dispatch({
            type: 'SET_MAP_LOCATION',
            center: urlLocation.center,
            zoom: urlLocation.zoom,
          });
        }
      };

      // Register for route change events
      router.events.on('routeChangeComplete', routeHandler);

      // Try to get location from URL first
      const urlLocation = parseLocationFromUrl(win.location.pathname);

      let initialLocation = null;

      if (urlLocation && urlLocation.center.lat !== 0 && urlLocation.center.lon !== 0) {
        // If valid URL location found, clear session storage
        try {
          win.sessionStorage.removeItem('slMapLocation');
        } catch (_err) {
          // Ignore session storage errors
        }

        // Capture the location to use for initialization
        initialLocation = urlLocation;

        // Update Redux state with URL location
        dispatch({
          type: 'SET_MAP_LOCATION',
          center: urlLocation.center,
          zoom: urlLocation.zoom,
        });
      }

      // Initialize the map with the correct initial location
      initializeMap();

      // Ensure we explicitly set the view to the correct location
      if (initialLocation && leafletMapRef.current) {
        leafletMapRef.current.setView(
          [initialLocation.center.lat, initialLocation.center.lon],
          initialLocation.zoom,
        );
      } else if (
        map.location.center &&
        map.location.center.lat &&
        map.location.center.lon &&
        leafletMapRef.current
      ) {
        // Fallback to the store state if no URL location
        leafletMapRef.current.setView(
          [map.location.center.lat, map.location.center.lon],
          map.location.zoom,
        );
      }

      // Ensure we call updateSpots after map is initialized to trigger loading state
      if (map.spots && map.spots.length > 0) {
        // If spots already exist in the store, we can set loading to false
        if (onLoadingChange) onLoadingChange(false);
      } else {
        // Otherwise, trigger spot data loading
        updateSpots();
      }

      // Cleanup event listeners on unmount
      return () => {
        router.events.off('routeChangeComplete', routeHandler);

        try {
          const videoElements = document.querySelectorAll('video');
          videoElements.forEach((video) => {
            if (video && video.parentNode) {
              const clone = video.cloneNode(false);
              video.parentNode.replaceChild(clone, video);
            }
          });
        } catch (error) {
          console.warn('Error cleaning up videos', error);
        }
      };
    }

    return undefined;
  });

  // Handle location updates - mimics componentDidUpdate behavior
  useEffect(() => {
    // Only run if map exists and we're not dragging
    if (!leafletMapRef.current || isDragging) return;

    // Get current map center
    const { lat, lng } = leafletMapRef.current.getCenter();

    // Check if redux location is different from current map view
    const newCenter = map.location.center;
    const newZoom = map.location.zoom;

    if (
      newCenter?.lat &&
      (newCenter.lat !== lat ||
        newCenter.lon !== lng ||
        newZoom !== leafletMapRef.current.getZoom())
    ) {
      // This matches the original class component's componentDidUpdate logic exactly
      isExternalChangeRef.current = true;
      leafletMapRef.current.setView([newCenter.lat ?? 0, newCenter.lon ?? 0], newZoom);
    }
  }, [map.location.center, map.location.zoom, isDragging]);

  // Handle active spot clearing
  useEffect(() => {
    // Only update the previous value reference when the active spot ID changes
    if (prevActiveSpotIdRef.current !== animations.activeSpotId) {
      prevActiveSpotIdRef.current = animations.activeSpotId;
    }
  }, [animations.activeSpotId]);

  // Minimal cleanup - let useMap hook handle the heavy lifting
  useEffect(
    () => () => {
      // Only clear the reference, don't cleanup the map instance
      // The useMap hook will handle proper cleanup when needed
      leafletMapRef.current = null;
    },
    [],
  );

  // Main component render
  return (
    <div className={styles.spotMap}>
      <div id="rasterMap" ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />

      <MapOverlay
        spots={map.spots}
        clusters={map.clusters}
        units={units as unknown as Units}
        animations={animations}
        permissions={permissions}
        getMarkerStyle={getMarkerStyle}
        getTooltipPosition={getTooltipPosition}
        doSetActiveSpot={doSetActiveSpot}
        doClearActiveSpot={doClearActiveSpot}
        doZoomIntoLocation={doZoomIntoLocation}
        onClickSpotMarker={onClickSpotMarker}
        syncUrl={syncUrl}
        currentSpotId={currentSpotId}
        leafletMapRef={leafletMapRef}
        isDragging={isDragging}
        isLoadingSpots={isLoadingSpots}
      />

      {showBottomControls ? (
        <MapControls
          activeMap={mapLayers.SURF_SPOTS}
          center={map.location.center}
          subregion={findSubregionById(map.regionalForecast.subregionId ?? '')}
          subregions={map.subregions}
          subregionId={map.regionalForecast.subregionId ?? ''}
        />
      ) : null}
    </div>
  );
};

SpotMapComponent.displayName = 'SpotMap';

// Use React.memo for the exported component
const SpotMap = React.memo(
  SpotMapComponent,
  (prevProps, nextProps) =>
    // Custom comparison function to prevent unnecessary re-renders
    // Only re-render if currentSpotId changed
    prevProps.currentSpotId === nextProps.currentSpotId &&
    prevProps.showBottomControls === nextProps.showBottomControls,
);

export default SpotMap;
